import { Position } from "@xyflow/react";
import { BaseNode, BaseNodeContent } from "../../components/base-node";
import { BaseHandle } from "../../components/base-handle";
import { ConditionNodeProps } from "./types";
import { cn } from "@babackapp/ui/lib/utils";
import { useWorkflowsEditorConfig } from "../../context/workflows-editor-config-context";
import { useTranslation } from "react-i18next";
import { useNodeData } from "../../hooks/use-node-data";
import { ConditionNodeDataDto } from "@babackapp/private-api-client/types";
import { useMemo } from "react";
import { useIsWorkflowsListingMode } from "../../context/workflows-editor-workflows-context";
import { ContextualTipsTrigger } from "@babackapp/ui/components/contextual-tips";
import { GitBranch } from "lucide-react";

export const CONDITION_NODE_WIDTH = 250;
export const CONDITION_NODE_HEIGHT_ESTIMATION = 150;

export function ConditionNodeEditor({ id, data }: ConditionNodeProps) {
  const liveData = useNodeData<ConditionNodeDataDto>(id);
  const nodeData = liveData ?? data;
  const conditionsCount = nodeData?.conditions?.length || 0;
  const hasConditions = conditionsCount > 0;
  const { t } = useTranslation("trackoffice");
  const { conditionFields } = useWorkflowsEditorConfig();

  // Build a human-readable summary of conditions
  const conditionsSummary = useMemo(() => {
    return nodeData?.conditions
      ?.map((c) => {
        if (!c.field || !c.operator) return null;
        const field = conditionFields.find((f) => f.value === c.field);
        const label = field?.label || c.field;
        const stringLabel = t(
          `workflowsEditor.node.condition.fields.${c.field}`,
          label,
        );
        const stringOperator = t(
          `workflowsEditor.node.condition.operator.${c.operator?.toLocaleLowerCase()}`,
          c.operator,
        );

        if (["is_false", "is_true"].includes(c.operator)) {
          return stringLabel;
        }

        return `${stringLabel} ${stringOperator} ${c.value || '""'}`;
      })
      .filter(Boolean);
  }, [nodeData, conditionFields, t]);

  const humanReadableSummary =
    (conditionsSummary && conditionsSummary.length
      ? conditionsSummary.join(
          ` ${t(
            `workflowsEditor.node.condition.operator.${(nodeData?.operator || "and").toLocaleLowerCase()}`,
            (nodeData?.operator || "AND").toLocaleLowerCase(),
          )} `,
        )
      : "") + " ?";

  return (
    <ContextualTipsTrigger
      tips={[
        {
          id: "workflow-node-condition",
          Icon: GitBranch,
          title: t(
            "workflowsEditor.tips.workflowNodeCondition.title",
            "Condition",
          ),
          description: t(
            "workflowsEditor.tips.workflowNodeCondition.desc",
            "This node allows you to define a condition that must be met for the next nodes to proceed. You can configure multiple conditions using the panel on the right.",
          ),
        },
      ]}
    >
      <BaseNode nodeId={id} style={{ position: "relative" }}>
        <BaseNodeContent className="pr-10">
          <div className="flex flex-col gap-2 w-full">
            <p className="p-2 text-md text-foreground/70 font-semibold">
              {hasConditions
                ? humanReadableSummary
                : t(
                    "workflowsEditor.node.condition.noConditionsConfigured",
                    "Cliquer pour ajouter une condition",
                  )}
            </p>
          </div>
          <BaseHandle
            type="target"
            position={Position.Left}
            style={{ top: "50%" }}
          />
          <BaseHandle
            type="source"
            position={Position.Right}
            id="true"
            style={{ top: "30%" }}
          >
            <HandleLabel
              label={t("workflowsEditor.node.condition.true")}
              className="bg-green-500/10 border-green-500 border px-1.5 text-green-500"
            />
          </BaseHandle>
          <BaseHandle
            type="source"
            position={Position.Right}
            id="false"
            style={{ top: "70%" }}
          >
            <HandleLabel
              label={t("workflowsEditor.node.condition.false")}
              className="bg-red-500/10 border-red-500 border px-1.5 text-red-500"
            />
          </BaseHandle>
        </BaseNodeContent>
      </BaseNode>
    </ContextualTipsTrigger>
  );
}

function HandleLabel({
  label,
  className,
}: {
  label: string;
  className?: string;
}) {
  const isWorkflowsListingMode = useIsWorkflowsListingMode();

  return (
    <div
      className={cn(
        "absolute right-0 top-0 -translate-y-1/2 text-xs font-semibold text-white bg-green-500 px-1 py-0.5",
        className,
        isWorkflowsListingMode
          ? "w-2 h-2  rounded-full"
          : "pr-4 w-10  rounded-l-full",
      )}
      style={{
        fontSize: "0.6rem",
        top: 2,
      }}
    >
      {!isWorkflowsListingMode && label}
    </div>
  );
}
